import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './AdminRevenueV2.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    ResponsiveContainer,
    ComposedChart,
    LineChart,
    BarChart,
    Line,
    Area,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
    ReferenceArea,
    ReferenceLine,
} from 'recharts';
import {
    formatInputDate,
    normalizeAnalyticsPayload,
    fillDailySeries,
    buildPieRows,
    formatAxisEUR,
    formatChartDateTick,
    formatRevenueEuro,
    formatPct,
    priorPeriodRange,
    aggregateWeekly,
    buildMarginSeries,
    marginChartYMax,
    buildOrdersSeries,
    buildWeeklyComparison,
    computeKpis,
    REVENUE_PIE_COLORS,
} from './revenueMetrics';
import {
    LINEAR_FORECAST_DAYS,
    GROSS_MOVING_AVG_DAYS,
    MAIN_Y_PERCENTILE,
    buildComposedChartData,
    getForecastBand,
} from './revenueForecast';

const DATE_PRESETS = [
    { id: '7d', label: '7 days', days: 6 },
    { id: '30d', label: '30 days', days: 29 },
    { id: '90d', label: '90 days', days: 89 },
    { id: 'ytd', label: 'YTD', ytd: true },
];

const CHART_TOOLTIP_STYLE = {
    borderRadius: 8,
    border: '1px solid #E8E0D6',
    fontFamily: 'Sora, sans-serif',
    fontSize: 12,
    maxWidth: 300,
    lineHeight: 1.35,
};

const fetchAnalytics = async (base, startDate, endDate) => {
    const params = new URLSearchParams({ startDate, endDate });
    const res = await fetch(`${base}/api/revenueAnalytics?${params.toString()}`);
    if (!res.ok) {
        throw new Error(res.status === 400 ? 'Invalid date range' : 'Could not load analytics');
    }
    return res.json();
};

const AdminRevenueV2 = () => {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 89);
        return formatInputDate(d);
    });
    const [endDate, setEndDate] = useState(() => formatInputDate(new Date()));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);
    const [priorPayload, setPriorPayload] = useState(null);
    const [dataNotice, setDataNotice] = useState(null);

    useEffect(() => {
        if (localStorage.getItem('account') == null || localStorage.getItem('admin') !== 'true') {
            navigate('/login');
        }
    }, [navigate]);

    const applyPreset = (preset) => {
        const end = new Date();
        let start;
        if (preset.ytd) {
            start = new Date(end.getFullYear(), 0, 1);
        } else {
            start = new Date(end);
            start.setDate(start.getDate() - preset.days);
        }
        setStartDate(formatInputDate(start));
        setEndDate(formatInputDate(end));
    };

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        setDataNotice(null);
        setPriorPayload(null);

        const base = process.env.REACT_APP_API;
        if (!base || base === 'undefined') {
            setLoading(false);
            setError(
                'REACT_APP_API is not set. Add it to .env (e.g. http://localhost:3030 for local API) and restart npm run dev.'
            );
            return;
        }

        const { priorStart, priorEnd } = priorPeriodRange(startDate, endDate);
        const fallbackRange = { start: startDate, end: endDate };

        Promise.all([
            fetchAnalytics(base, startDate, endDate),
            fetchAnalytics(base, priorStart, priorEnd).catch(() => null),
        ])
            .then(([current, prior]) => {
                const normalized = normalizeAnalyticsPayload(current, fallbackRange);
                setPayload(normalized);
                if (prior) {
                    setPriorPayload(normalizeAnalyticsPayload(prior, { start: priorStart, end: priorEnd }));
                }
                if (normalized._invalidShape) {
                    const hint =
                        'The API returned an unexpected response (this often means production is not deployed with ' +
                        '/api/revenueAnalytics yet, or the URL is wrong). Use REACT_APP_API=http://localhost:3030 in ' +
                        '.env while the local backend is running, then restart npm run dev — or deploy the latest backend.';
                    setDataNotice(hint);
                    toast.error('Revenue API response was not valid JSON analytics data');
                } else if (normalized._apiError) {
                    setDataNotice(normalized._apiError);
                }
            })
            .catch((err) => {
                setError(err.message || 'Request failed');
                toast.error('Failed to load revenue analytics');
            })
            .finally(() => setLoading(false));
    }, [startDate, endDate]);

    useEffect(() => {
        load();
    }, [load]);

    const chartData = useMemo(() => {
        if (!payload?.range || !payload?.series) return [];
        return fillDailySeries(payload.range.start, payload.range.end, payload.series);
    }, [payload]);

    const priorChartData = useMemo(() => {
        if (!priorPayload?.range || !priorPayload?.series) return [];
        return fillDailySeries(priorPayload.range.start, priorPayload.range.end, priorPayload.series);
    }, [priorPayload]);

    const composed = useMemo(() => buildComposedChartData(chartData), [chartData]);
    const { composedRows, grossAxisCapped, mainYMax, marginYMax, forecastMeta } = composed;
    const forecastBand = useMemo(() => getForecastBand(composedRows), [composedRows]);
    const pieRows = useMemo(() => buildPieRows(payload?.summary), [payload]);
    const weeklyRows = useMemo(() => aggregateWeekly(chartData), [chartData]);
    const priorWeeklyRows = useMemo(() => aggregateWeekly(priorChartData), [priorChartData]);
    const marginRows = useMemo(() => buildMarginSeries(chartData), [chartData]);
    const ordersRows = useMemo(() => buildOrdersSeries(chartData), [chartData]);
    const comparisonRows = useMemo(
        () => buildWeeklyComparison(weeklyRows, priorWeeklyRows),
        [weeklyRows, priorWeeklyRows]
    );

    const kpis = useMemo(
        () => computeKpis(payload?.summary, payload?.range, priorPayload?.summary ?? null),
        [payload, priorPayload]
    );

    const summary = payload?.summary;
    const showDashboard = !loading && !error && payload && summary;
    const periodMarginPct =
        summary && summary.grossRevenue > 0 ? (summary.profit / summary.grossRevenue) * 100 : 0;
    const maxOrders = ordersRows.length ? Math.max(...ordersRows.map((r) => r.orderCount), 1) : 1;
    const marginPctYMax = marginChartYMax(marginRows, periodMarginPct);
    const maxWeeklyGross = weeklyRows.reduce((m, r) => Math.max(m, r.grossRevenue), 1);
    const maxComparisonGross = comparisonRows.reduce(
        (m, r) => Math.max(m, r.currentGross, r.priorGross),
        1
    );

    return (
        <div className="revenuev2">
            <NavbarV2 />
            <header className="revenuev2-header">
                <h1 className="revenuev2-title">Business dashboard</h1>
                <p className="revenuev2-subtitle">
                    Revenue, margin, and order volume for Sicily. Declined orders are excluded; gross revenue is product
                    subtotal + shipping + surcharge. The trend line uses a stable weekly model (not raw daily regression).
                </p>
            </header>

            <div className="revenuev2-controls">
                <div className="revenuev2-presets">
                    {DATE_PRESETS.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            className="revenuev2-preset"
                            onClick={() => applyPreset(p)}
                            disabled={loading}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="revenuev2-field">
                    <label htmlFor="rev-start">From</label>
                    <input
                        id="rev-start"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </div>
                <div className="revenuev2-field">
                    <label htmlFor="rev-end">To</label>
                    <input
                        id="rev-end"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                <button type="button" className="revenuev2-btn" onClick={load} disabled={loading}>
                    Refresh
                </button>
                <button type="button" className="revenuev2-btn revenuev2-btn--ghost" onClick={() => navigate('/admin')}>
                    Back to admin
                </button>
            </div>

            <main className="revenuev2-main">
                {loading && <div className="revenuev2-loading">Loading…</div>}
                {error && !loading && <div className="revenuev2-error">{error}</div>}
                {dataNotice && !loading && !error && (
                    <div className="revenuev2-notice" role="status">
                        {dataNotice}
                    </div>
                )}
                {showDashboard && (
                    <>
                        <div className="revenuev2-stats">
                            <div className="revenuev2-stat">
                                <p className="revenuev2-stat-label">Gross revenue</p>
                                <p className="revenuev2-stat-value">{formatRevenueEuro(kpis.gross)}</p>
                                {kpis.grossDeltaPct && (
                                    <p className="revenuev2-stat-delta">{kpis.grossDeltaPct} vs prior period</p>
                                )}
                                <p className="revenuev2-stat-hint">
                                    {formatRevenueEuro(kpis.dailyAvgGross)}/day over {kpis.days} days
                                </p>
                            </div>
                            <div className="revenuev2-stat revenuev2-stat--highlight">
                                <p className="revenuev2-stat-label">Margin (shipping + surcharges)</p>
                                <p className="revenuev2-stat-value revenuev2-stat-value--profit">
                                    {formatRevenueEuro(kpis.profit)}
                                </p>
                                <p className="revenuev2-stat-hint">
                                    {formatPct(kpis.marginPct)} of gross · {formatRevenueEuro(kpis.dailyAvgProfit)}/day
                                </p>
                            </div>
                            <div className="revenuev2-stat">
                                <p className="revenuev2-stat-label">Orders</p>
                                <p className="revenuev2-stat-value">{kpis.orders}</p>
                                {kpis.ordersDeltaPct && (
                                    <p className="revenuev2-stat-delta">{kpis.ordersDeltaPct} vs prior period</p>
                                )}
                                <p className="revenuev2-stat-hint">
                                    {kpis.dailyAvgOrders.toFixed(1)} orders/day · AOV {formatRevenueEuro(kpis.aov)}
                                </p>
                            </div>
                            <div className="revenuev2-stat">
                                <p className="revenuev2-stat-label">Product subtotal</p>
                                <p className="revenuev2-stat-value">{formatRevenueEuro(summary.cartTotal)}</p>
                                <p className="revenuev2-stat-hint">
                                    {summary.grossRevenue > 0
                                        ? `${formatPct((summary.cartTotal / summary.grossRevenue) * 100)} of gross`
                                        : 'No revenue in range'}
                                </p>
                            </div>
                        </div>

                        <div className="revenuev2-chart-card revenuev2-chart-card--full">
                                <h2 className="revenuev2-chart-title">Daily revenue trend</h2>
                                <p className="revenuev2-chart-desc">
                                    Gross uses a {GROSS_MOVING_AVG_DAYS}-day average with raw daily overlay. The dashed
                                    line projects from today&apos;s smoothed run-rate using median week-on-week growth
                                    (complete weeks only, capped at ±2.5%/week). Extends ~{Math.round(LINEAR_FORECAST_DAYS / 30)} months ahead.
                                </p>
                                <div className="revenuev2-chart-wrap">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={composedRows} margin={{ top: 18, right: 50, left: 6, bottom: 58 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DA" strokeOpacity={0.95} vertical={false} />
                                            {forecastBand && (
                                                <ReferenceArea
                                                    x1={forecastBand.x1}
                                                    x2={forecastBand.x2}
                                                    yAxisId="main"
                                                    fill="#DDD4C8"
                                                    fillOpacity={0.16}
                                                    ifOverflow="visible"
                                                    strokeOpacity={0}
                                                />
                                            )}
                                            {chartData.length > 0 && (
                                                <ReferenceLine
                                                    yAxisId="main"
                                                    x={chartData[chartData.length - 1].date}
                                                    stroke="#B8ADA2"
                                                    strokeDasharray="4 7"
                                                    strokeWidth={1}
                                                />
                                            )}
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fill: '#5C544E', fontSize: 11 }}
                                                axisLine={{ stroke: '#D9D1C7' }}
                                                tickLine={{ stroke: '#D9D1C7' }}
                                                tickMargin={6}
                                                minTickGap={56}
                                                tickFormatter={formatChartDateTick}
                                                angle={-32}
                                                textAnchor="end"
                                                height={52}
                                                interval="preserveStartEnd"
                                            />
                                            <YAxis
                                                yAxisId="main"
                                                orientation="left"
                                                domain={mainYMax != null ? [0, mainYMax] : undefined}
                                                tick={{ fill: '#5C544E', fontSize: 11 }}
                                                axisLine={{ stroke: '#D9D1C7' }}
                                                tickLine={{ stroke: '#D9D1C7' }}
                                                tickFormatter={formatAxisEUR}
                                                width={54}
                                            />
                                            <YAxis
                                                yAxisId="margin"
                                                orientation="right"
                                                domain={marginYMax != null ? [0, marginYMax] : undefined}
                                                tick={{ fill: '#5D553E', fontSize: 11 }}
                                                axisLine={{ stroke: '#D9D1C7' }}
                                                tickLine={{ stroke: '#D9D1C7' }}
                                                tickFormatter={formatAxisEUR}
                                                width={54}
                                            />
                                            <Tooltip
                                                formatter={(value, name) => {
                                                    if (value == null || name == null || Number.isNaN(Number(value))) {
                                                        return null;
                                                    }
                                                    return [formatRevenueEuro(value), name];
                                                }}
                                                labelFormatter={(label, items) => {
                                                    const human =
                                                        typeof label === 'string' ? formatChartDateTick(label) : label;
                                                    return items?.[0]?.payload?.isForecast === true
                                                        ? `${human} (projection)`
                                                        : human;
                                                }}
                                                contentStyle={CHART_TOOLTIP_STYLE}
                                            />
                                            <Legend
                                                wrapperStyle={{ fontSize: 12 }}
                                                verticalAlign="top"
                                                align="center"
                                                height={42}
                                                iconType="circle"
                                                iconSize={8}
                                            />
                                            <Area
                                                yAxisId="margin"
                                                type="monotone"
                                                dataKey="shipping"
                                                name="Shipping"
                                                stackId="margin"
                                                fill="#6B7C3E"
                                                stroke="#5a6a32"
                                                strokeWidth={1}
                                                fillOpacity={0.92}
                                            />
                                            <Area
                                                yAxisId="margin"
                                                type="monotone"
                                                dataKey="surcharge"
                                                name="Surcharge"
                                                stackId="margin"
                                                fill="#D4785A"
                                                stroke="#C4603C"
                                                strokeWidth={1}
                                                fillOpacity={0.92}
                                            />
                                            <Line
                                                yAxisId="main"
                                                type="linear"
                                                dataKey="trendGross"
                                                name="Stable trend (projection)"
                                                stroke="#665E58"
                                                strokeWidth={2.25}
                                                strokeDasharray="8 5"
                                                dot={false}
                                                isAnimationActive={false}
                                                connectNulls
                                                legendType="line"
                                            />
                                            <Line
                                                yAxisId="main"
                                                type="monotone"
                                                dataKey="grossRevenue"
                                                name="Daily gross"
                                                legendType="none"
                                                stroke="#1A1714"
                                                strokeOpacity={0.38}
                                                strokeWidth={1}
                                                dot={false}
                                                isAnimationActive={false}
                                                connectNulls={false}
                                            />
                                            <Line
                                                yAxisId="main"
                                                type="monotone"
                                                dataKey="grossMa7"
                                                name={`Gross (${GROSS_MOVING_AVG_DAYS}-day avg)`}
                                                stroke="#1A1714"
                                                strokeWidth={2.85}
                                                dot={false}
                                                isAnimationActive={false}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                {grossAxisCapped && (
                                    <p className="revenuev2-chart-footnote" role="note">
                                        Some exceptionally high gross days extend above this left axis (~
                                        {Math.round(MAIN_Y_PERCENTILE * 100)}th percentile cap). Hover for exact values.
                                    </p>
                                )}
                        </div>

                        <div className="revenuev2-grid-2">
                            <div className="revenuev2-chart-card">
                                <h2 className="revenuev2-chart-title">Revenue mix</h2>
                                <p className="revenuev2-chart-desc">Share of product total vs shipping vs surcharge.</p>
                                <div className="revenuev2-chart-wrap revenuev2-chart-wrap--pie">
                                    {pieRows.length === 0 ? (
                                        <div className="revenuev2-empty">No revenue in this range.</div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={pieRows}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={64}
                                                    outerRadius={100}
                                                    paddingAngle={2}
                                                >
                                                    {pieRows.map((entry) => (
                                                        <Cell
                                                            key={entry.name}
                                                            fill={REVENUE_PIE_COLORS[entry.name] || '#888'}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(value) => formatRevenueEuro(value)} contentStyle={CHART_TOOLTIP_STYLE} />
                                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>

                            {forecastMeta?.hasForecast && (
                                <div className="revenuev2-forecast-strip">
                                    <div className="revenuev2-forecast-item">
                                        <span className="revenuev2-forecast-label">Weekly growth</span>
                                        <span className="revenuev2-forecast-value">
                                            {forecastMeta.growthPctDisplay >= 0 ? '+' : ''}
                                            {forecastMeta.growthPctDisplay.toFixed(2)}%/week
                                        </span>
                                    </div>
                                    <div className="revenuev2-forecast-item">
                                        <span className="revenuev2-forecast-label">Smoothed daily level</span>
                                        <span className="revenuev2-forecast-value">
                                            {formatRevenueEuro(forecastMeta.level)}/day
                                        </span>
                                    </div>
                                    <div className="revenuev2-forecast-item">
                                        <span className="revenuev2-forecast-label">~6-month projection</span>
                                        <span className="revenuev2-forecast-value">
                                            {formatRevenueEuro(forecastMeta.projected6mo)}/day
                                        </span>
                                    </div>
                                    <p className="revenuev2-forecast-note">
                                        Based on median week-on-week change from complete weeks; growth fades toward flat
                                        over longer horizons so the line does not crash to zero.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="revenuev2-grid-2">
                            <div className="revenuev2-chart-card">
                                <h2 className="revenuev2-chart-title">Weekly revenue</h2>
                                <p className="revenuev2-chart-desc">Stacked weekly totals — product, shipping, and surcharge.</p>
                                <div className="revenuev2-chart-wrap revenuev2-chart-wrap--medium">
                                    {weeklyRows.length < 2 ? (
                                        <div className="revenuev2-empty">Need at least two weeks of data.</div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={weeklyRows} margin={{ top: 12, right: 12, left: 6, bottom: 48 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DA" vertical={false} />
                                                <XAxis
                                                    dataKey="weekLabel"
                                                    tick={{ fill: '#5C544E', fontSize: 11 }}
                                                    angle={-28}
                                                    textAnchor="end"
                                                    height={48}
                                                    interval="preserveStartEnd"
                                                />
                                                <YAxis
                                                    domain={[0, Math.ceil(maxWeeklyGross * 1.12)]}
                                                    tickFormatter={formatAxisEUR}
                                                    tick={{ fill: '#5C544E', fontSize: 11 }}
                                                    width={54}
                                                />
                                                <Tooltip
                                                    formatter={(value) => formatRevenueEuro(value)}
                                                    contentStyle={CHART_TOOLTIP_STYLE}
                                                />
                                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                                <Bar dataKey="cartTotal" name="Product" stackId="rev" fill="#1A1714" />
                                                <Bar dataKey="shipping" name="Shipping" stackId="rev" fill="#6B7C3E" />
                                                <Bar dataKey="surcharge" name="Surcharge" stackId="rev" fill="#C4603C" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>

                            <div className="revenuev2-chart-card">
                                <h2 className="revenuev2-chart-title">Order volume</h2>
                                <p className="revenuev2-chart-desc">
                                    Daily orders with {GROSS_MOVING_AVG_DAYS}-day average. Dashed line = period average.
                                </p>
                                <div className="revenuev2-chart-wrap revenuev2-chart-wrap--medium">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={ordersRows} margin={{ top: 12, right: 12, left: 6, bottom: 48 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DA" vertical={false} />
                                            <XAxis
                                                dataKey="date"
                                                tickFormatter={formatChartDateTick}
                                                tick={{ fill: '#5C544E', fontSize: 11 }}
                                                angle={-28}
                                                textAnchor="end"
                                                height={48}
                                                interval="preserveStartEnd"
                                                minTickGap={48}
                                            />
                                            <YAxis
                                                domain={[0, Math.ceil(maxOrders * 1.15)]}
                                                allowDecimals={false}
                                                tick={{ fill: '#5C544E', fontSize: 11 }}
                                                width={40}
                                            />
                                            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            {kpis.orders > 0 && (
                                                <ReferenceLine
                                                    y={kpis.dailyAvgOrders}
                                                    stroke="#B8ADA2"
                                                    strokeDasharray="4 7"
                                                    label={{ value: 'Avg/day', fill: '#7A7168', fontSize: 10 }}
                                                />
                                            )}
                                            <Area
                                                type="monotone"
                                                dataKey="orderCount"
                                                name="Daily orders"
                                                fill="#1A1714"
                                                fillOpacity={0.08}
                                                stroke="#1A1714"
                                                strokeOpacity={0.35}
                                                strokeWidth={1}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="ordersMa7"
                                                name={`Orders (${GROSS_MOVING_AVG_DAYS}-day avg)`}
                                                stroke="#1A1714"
                                                strokeWidth={2.5}
                                                dot={false}
                                                isAnimationActive={false}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        <div className="revenuev2-grid-2">
                            <div className="revenuev2-chart-card">
                                <h2 className="revenuev2-chart-title">Margin rate</h2>
                                <p className="revenuev2-chart-desc">
                                    Fee margin as % of gross (shipping + surcharge ÷ gross). Period avg:{' '}
                                    {formatPct(periodMarginPct)}.
                                </p>
                                <div className="revenuev2-chart-wrap revenuev2-chart-wrap--medium">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={marginRows} margin={{ top: 12, right: 12, left: 6, bottom: 48 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DA" vertical={false} />
                                            <XAxis
                                                dataKey="date"
                                                tickFormatter={formatChartDateTick}
                                                tick={{ fill: '#5C544E', fontSize: 11 }}
                                                angle={-28}
                                                textAnchor="end"
                                                height={48}
                                                interval="preserveStartEnd"
                                                minTickGap={48}
                                            />
                                            <YAxis
                                                domain={[0, marginPctYMax]}
                                                tickFormatter={(v) => `${v}%`}
                                                tick={{ fill: '#5C544E', fontSize: 11 }}
                                                width={44}
                                                allowDataOverflow={false}
                                            />
                                            <Tooltip
                                                formatter={(value, name) => {
                                                    if (value == null) return null;
                                                    if (name?.includes('%') || name?.includes('Margin')) {
                                                        return [`${Number(value).toFixed(1)}%`, name];
                                                    }
                                                    return [formatRevenueEuro(value), name];
                                                }}
                                                contentStyle={CHART_TOOLTIP_STYLE}
                                            />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <ReferenceLine
                                                y={periodMarginPct}
                                                stroke="#6B7C3E"
                                                strokeDasharray="4 7"
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="marginPct"
                                                name="Daily margin %"
                                                stroke="#1A1714"
                                                strokeOpacity={0.3}
                                                strokeWidth={1}
                                                dot={false}
                                                connectNulls={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="marginPctMa7"
                                                name={`Margin % (${GROSS_MOVING_AVG_DAYS}-day avg)`}
                                                stroke="#6B7C3E"
                                                strokeWidth={2.5}
                                                dot={false}
                                                connectNulls={false}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="revenuev2-chart-card">
                                <h2 className="revenuev2-chart-title">Period comparison</h2>
                                <p className="revenuev2-chart-desc">
                                    Current range vs prior {kpis.days} days (week-by-week gross).
                                </p>
                                <div className="revenuev2-chart-wrap revenuev2-chart-wrap--medium">
                                    {!priorPayload || comparisonRows.length === 0 ? (
                                        <div className="revenuev2-empty">Prior period data unavailable.</div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={comparisonRows}
                                                margin={{ top: 12, right: 12, left: 6, bottom: 48 }}
                                                barGap={4}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DA" vertical={false} />
                                                <XAxis
                                                    dataKey="weekLabel"
                                                    tick={{ fill: '#5C544E', fontSize: 11 }}
                                                    angle={-28}
                                                    textAnchor="end"
                                                    height={48}
                                                />
                                                <YAxis
                                                    domain={[0, Math.ceil(maxComparisonGross * 1.12)]}
                                                    tickFormatter={formatAxisEUR}
                                                    tick={{ fill: '#5C544E', fontSize: 11 }}
                                                    width={54}
                                                />
                                                <Tooltip
                                                    formatter={(value) => formatRevenueEuro(value)}
                                                    contentStyle={CHART_TOOLTIP_STYLE}
                                                />
                                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                                <Bar dataKey="currentGross" name="Current period" fill="#1A1714" radius={[4, 4, 0, 0]} />
                                                <Bar dataKey="priorGross" name="Prior period" fill="#B8ADA2" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};

export default AdminRevenueV2;
